import type { ApiKeyResponse, ApiKeyScope } from '@/lib/api/types';

/**
 * Mirrors `MAX_ACTIVE_KEYS` in backend/src/api/services/api_keys.py. The
 * backend enforces it (409 `active_key_limit`); this only lets the UI say so
 * before the user fills in a form.
 */
export const MAX_ACTIVE_API_KEYS = 2;

/** Mirrors `NAME_MAX_LENGTH`; the backend trims before checking. */
export const API_KEY_NAME_MAX_LENGTH = 100;

export const PUBLIC_API_BASE_URL = 'https://api.rii.link/v1';
export const API_DOCS_URL = 'https://rii.link/en/docs';

export type ApiKeyStatus = 'active' | 'revoked';
export type ApiKeyAccess = 'read' | 'read_write';

export function apiKeyStatus(key: ApiKeyResponse): ApiKeyStatus {
  return key.revoked_at ? 'revoked' : 'active';
}

export function activeKeyCount(keys: ApiKeyResponse[] | undefined): number {
  return (keys ?? []).filter((key) => apiKeyStatus(key) === 'active').length;
}

export function canCreateAnotherKey(keys: ApiKeyResponse[] | undefined): boolean {
  return activeKeyCount(keys) < MAX_ACTIVE_API_KEYS;
}

/** The backend rejects any scope set without workspace:read. */
export function scopesForAccess(access: ApiKeyAccess): ApiKeyScope[] {
  return access === 'read_write' ? ['workspace:read', 'workspace:write'] : ['workspace:read'];
}

export function accessForScopes(scopes: ApiKeyScope[]): ApiKeyAccess {
  return scopes.includes('workspace:write') ? 'read_write' : 'read';
}

/** Returns an error message, or null when the backend will accept the name. */
export function validateApiKeyName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Give the key a name so you can recognise it later.';
  if (trimmed.length > API_KEY_NAME_MAX_LENGTH) {
    return `Names can be at most ${API_KEY_NAME_MAX_LENGTH} characters.`;
  }
  return null;
}

/** True for an axios-style error whose response status is 403. */
export function isForbiddenError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return false;
  return (response as { status?: unknown }).status === 403;
}
